import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Group,
  Burger,
  Drawer,
  Menu,
  Avatar,
  UnstyledButton,
  Text,
  Indicator,
  Box,
  Stack,
  Divider,
  Button,
  Collapse,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  Bell,
  ChevronDown,
  LogOut,
  Sparkles,
  Building2,
  Home,
  Info,
  PhoneCall,
  LogIn,
} from "lucide-react";

import Logo from "../../assets/images/logo.png";
import { logout } from "../../features/auth/authActions";
import Api from "../../admin/components/api/Api";
import { setFormData, resetStep } from "../../features/formData/formSlice";
import {
  ADMIN_PANEL_HOME,
  getUserRoles,
  getUserTypeName,
  isAdminUser,
} from "../../admin/navigation/adminNavigation";
import "./MainNavigation.css";

function MainNavigation() {
  const loggedInUser = useSelector((state) => state.auth.user);
  const userType = getUserTypeName(loggedInUser);
  const roles = getUserRoles(loggedInUser);
  const adminUser = isAdminUser(loggedInUser);

  const [userDetail, setUserDetails] = useState(null);
  const [notificationCount, setNotificationCount] = useState(null);
  const [timestamp, setTimestamp] = useState(Date.now());
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdownOpened, setServicesDropdownOpened] = useState(false);
  const [mobileServicesOpened, setMobileServicesOpened] = useState(true);

  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const id = loggedInUser?.user?.id;
  const customerDashboardPath = "/customer/workspace";
  const partnerDashboardPath = "/partner/workspace";
  const customerProfilePath = "/customer/profile";
  const dashboardPath =
    userType === "CUSTOMER"
      ? customerDashboardPath
      : userType === "PARTNER"
      ? partnerDashboardPath
      : "/admin";
  const profilePath = userType === "CUSTOMER" ? customerProfilePath : "/user/profile";

  const getDetailsApi = Api({ url: `admin/user/id/${id}` });
  const getNotificationApi = Api({ url: `notify/getCount` });

  useEffect(() => {
    if (id) {
      getDetailsApi
        .fetchData()
        .then((responseData) => setUserDetails(responseData?.data))
        .catch((error) => console.error("Error fetching data:", error));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, timestamp]);

  useEffect(() => {
    const getNotificationData = async () => {
      try {
        const responseData = await getNotificationApi.fetchDataBySingleId();
        if (responseData?.data?.statusType === "SUCCESS") {
          setNotificationCount(responseData.data.totalRecord);
        }
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };
    getNotificationData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTimestamp(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    closeDrawer();
    setServicesDropdownOpened(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logoutHandler = async () => {
    try {
      dispatch(resetStep());
      dispatch(setFormData({}));
      dispatch(logout());
      navigate(adminUser ? "/admin-login" : "/login");
    } catch (error) {
      console.log(error);
    }
  };

  const navLinks = [
    {
      to: dashboardPath,
      label: "Dashboard",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: "/post",
      label: "Posts",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/property",
      label: "Properties",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/promotion-list",
      label: "Promotions",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE", "PARTNER"],
      role: [],
    },
    {
      to: "/query",
      label: "Queries",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/help-center",
      label: "Help Center",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: "/user",
      label: "Masters",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE", "PARTNER"],
      role: ["ROLE_ADMIN"],
    },
    {
      to: "my-preference",
      label: "Preference",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: profilePath,
      label: "Profile",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
  ];

  const getFilteredLinks = () =>
    adminUser
      ? [
          { to: ADMIN_PANEL_HOME, label: "Admin Panel" },
          { to: profilePath, label: "Profile" },
        ]
      : navLinks.filter((link) => {
      const isAllowedUser = link.allowedUserTypes.includes(userType);
      const isPartnerAdmin =
        userType === "PARTNER" &&
        roles?.includes("ROLE_ADMIN") &&
        link.allowedUserTypes.includes("PARTNER");
      const isPartnerVisible =
        userType !== "PARTNER" ||
        link.role.length === 0 ||
        roles?.includes("ROLE_ADMIN");
      return (isAllowedUser || isPartnerAdmin) && isPartnerVisible;
    });

  // Transparent ONLY on specific pages before scroll — solid everywhere else
  const TRANSPARENT_ROUTES = new Set([
    "/",
    "/about-us",
    "/interiordesign",
    "/property-services",
    "/policy",
    "/contactus",
  ]);

  const isTransparentRoute = TRANSPARENT_ROUTES.has(location.pathname);
  const isTransparent = isTransparentRoute && !scrolled && !drawerOpened;
  const isServicesActive =
    location.pathname === "/interiordesign" ||
    location.pathname === "/property-services";

  const navClass = `main-nav ${
    isTransparent ? "main-nav--transparent" : "main-nav--solid"
  }`;

  return (
    <>
      <header className={navClass}>
        <Container size="xl" h="100%">
          <Group justify="space-between" h="100%">
            <Link to="/" className="main-nav__logo" aria-label="All Home Desire Home">
              <img src={Logo} alt="All Home Desire Logo" />
            </Link>

            {/* Desktop links: Home | About Us | Services ▼ | Contact Us */}
            <Group gap={8} visibleFrom="lg" className="main-nav__links-group">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                Home
              </NavLink>

              <NavLink
                to="/about-us"
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                About Us
              </NavLink>

              {/* Services Dropdown */}
              <Menu
                trigger="hover"
                openDelay={80}
                closeDelay={220}
                shadow="xl"
                width={310}
                position="bottom-start"
                offset={10}
                opened={servicesDropdownOpened}
                onChange={setServicesDropdownOpened}
                transitionProps={{ transition: "pop-top-left", duration: 180 }}
              >
                <Menu.Target>
                  <UnstyledButton
                    className={`main-nav__link main-nav__services-btn${
                      isServicesActive ? " active" : ""
                    }`}
                    aria-expanded={servicesDropdownOpened}
                    aria-haspopup="menu"
                  >
                    <span>Services</span>
                    <ChevronDown
                      size={14}
                      className={`main-nav__chevron ${
                        servicesDropdownOpened ? "open" : ""
                      }`}
                    />
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown className="main-nav__dropdown-menu">
                  <div className="main-nav__dropdown-header">
                    <span className="main-nav__dropdown-eyebrow">OUR SERVICES</span>
                  </div>

                  <Menu.Item
                    component={Link}
                    to="/interiordesign"
                    className={`main-nav__dropdown-item ${
                      location.pathname === "/interiordesign" ? "selected" : ""
                    }`}
                  >
                    <div className="main-nav__dropdown-row">
                      <div className="main-nav__dropdown-icon-box">
                        <Sparkles size={18} className="main-nav__dropdown-icon" />
                      </div>
                      <div className="main-nav__dropdown-info">
                        <div className="main-nav__dropdown-title">
                          Interior Design
                        </div>
                        <div className="main-nav__dropdown-sub">
                          Transform your space
                        </div>
                      </div>
                    </div>
                  </Menu.Item>

                  <Menu.Item
                    component={Link}
                    to="/property-services"
                    className={`main-nav__dropdown-item ${
                      location.pathname === "/property-services" ? "selected" : ""
                    }`}
                  >
                    <div className="main-nav__dropdown-row">
                      <div className="main-nav__dropdown-icon-box">
                        <Building2 size={18} className="main-nav__dropdown-icon" />
                      </div>
                      <div className="main-nav__dropdown-info">
                        <div className="main-nav__dropdown-title">
                          Property Services
                        </div>
                        <div className="main-nav__dropdown-sub">
                          Complete property solutions
                        </div>
                      </div>
                    </div>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <NavLink
                to="/contactus"
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                Contact Us
              </NavLink>
            </Group>

            {/* Right side: Login or User profile */}
            <Group gap={12}>
              {loggedInUser?.statusType === "SUCCESS" ? (
                <>
                  <Indicator
                    inline
                    label={notificationCount}
                    size={16}
                    disabled={!notificationCount || notificationCount === 0}
                    color="red"
                    offset={4}
                  >
                    <Link
                      to="/notification"
                      className="main-nav__notification-link"
                      aria-label="Notifications"
                    >
                      <Bell size={20} strokeWidth={1.8} />
                    </Link>
                  </Indicator>

                  <Menu shadow="md" width={220} position="bottom-end">
                    <Menu.Target>
                      <UnstyledButton className="main-nav__avatar-btn">
                        <Group gap={8}>
                          <Avatar
                            src={
                              (() => {
                                const photo =
                                  userDetail?.user?.photo ||
                                  userDetail?.photo ||
                                  userDetail?.image_url ||
                                  userDetail?.profile_photo ||
                                  loggedInUser?.user?.photo ||
                                  loggedInUser?.user?.image_url ||
                                  loggedInUser?.user?.profile_photo;
                                if (!photo) return null;
                                if (
                                  photo.startsWith("http://") ||
                                  photo.startsWith("https://")
                                )
                                  return photo;
                                const base = (
                                  userDetail?.assetUrl ||
                                  loggedInUser?.assetUrl ||
                                  process.env.REACT_APP_ASSET_URL ||
                                  "https://images.allhomedesire.in"
                                ).replace(/\/+$/, "");
                                return `${base}${
                                  photo.startsWith("/") ? "" : "/"
                                }${photo}`;
                              })()
                            }
                            radius="xl"
                            size={34}
                            color="blue"
                          >
                            {(
                              userDetail?.user?.first_name ||
                              userDetail?.first_name ||
                              loggedInUser?.user?.first_name ||
                              "C"
                            )
                              ?.charAt(0)
                              ?.toUpperCase()}
                          </Avatar>
                          <Box visibleFrom="sm">
                            <Text className="main-nav__avatar-name">
                              {userDetail?.user?.first_name ||
                                loggedInUser?.user?.first_name ||
                                "My Account"}
                            </Text>
                          </Box>
                          <ChevronDown size={13} style={{ opacity: 0.5 }} />
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>My Account</Menu.Label>
                      {getFilteredLinks().map((link, index) => (
                        <Menu.Item key={index} component={Link} to={link.to}>
                          {link.label}
                        </Menu.Item>
                      ))}
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<LogOut size={14} />}
                        onClick={logoutHandler}
                      >
                        Logout
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              ) : (
                <Group gap={10} visibleFrom="xs">
                  <Button
                    component={Link}
                    to="/login"
                    variant="filled"
                    size="sm"
                    className="main-nav__login-btn"
                  >
                    Login
                  </Button>
                </Group>
              )}

              <Burger
                opened={drawerOpened}
                onClick={toggleDrawer}
                hiddenFrom="lg"
                size="sm"
                aria-label="Toggle navigation menu"
                color={isTransparent ? "#fff" : "#0f172a"}
              />
            </Group>
          </Group>
        </Container>
      </header>

      {/* Mobile drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="lg"
        title={
          <Link to="/" onClick={closeDrawer}>
            <img src={Logo} alt="All Home Desire" style={{ height: "34px" }} />
          </Link>
        }
        hiddenFrom="lg"
        zIndex={1000}
        classNames={{
          content: "main-nav__drawer-content",
          header: "main-nav__drawer-header",
        }}
      >
        <Stack gap="xs" pt="sm">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            <Group gap={10}>
              <Home size={18} className="main-nav__drawer-icon" />
              <span>Home</span>
            </Group>
          </NavLink>

          <NavLink
            to="/about-us"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            <Group gap={10}>
              <Info size={18} className="main-nav__drawer-icon" />
              <span>About Us</span>
            </Group>
          </NavLink>

          {/* Services Accordion in Mobile */}
          <div className="main-nav__mobile-services-wrapper">
            <UnstyledButton
              className={`main-nav__drawer-link main-nav__drawer-accordion-btn ${
                isServicesActive ? "active" : ""
              }`}
              onClick={() => setMobileServicesOpened((prev) => !prev)}
            >
              <Group justify="space-between" w="100%">
                <Group gap={10}>
                  <Building2 size={18} className="main-nav__drawer-icon" />
                  <span>Services</span>
                </Group>
                <ChevronDown
                  size={16}
                  className={`main-nav__drawer-chevron ${
                    mobileServicesOpened ? "open" : ""
                  }`}
                />
              </Group>
            </UnstyledButton>

            <Collapse in={mobileServicesOpened}>
              <div className="main-nav__mobile-sublinks">
                <NavLink
                  to="/interiordesign"
                  className={({ isActive }) =>
                    `main-nav__mobile-sublink${isActive ? " active" : ""}`
                  }
                  onClick={closeDrawer}
                >
                  <div className="main-nav__mobile-sublink-inner">
                    <div className="main-nav__mobile-sublink-icon-box">
                      <Sparkles size={16} />
                    </div>
                    <div>
                      <div className="main-nav__mobile-sublink-title">
                        Interior Design
                      </div>
                      <div className="main-nav__mobile-sublink-desc">
                        Transform your space
                      </div>
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/property-services"
                  className={({ isActive }) =>
                    `main-nav__mobile-sublink${isActive ? " active" : ""}`
                  }
                  onClick={closeDrawer}
                >
                  <div className="main-nav__mobile-sublink-inner">
                    <div className="main-nav__mobile-sublink-icon-box">
                      <Building2 size={16} />
                    </div>
                    <div>
                      <div className="main-nav__mobile-sublink-title">
                        Property Services
                      </div>
                      <div className="main-nav__mobile-sublink-desc">
                        Complete property solutions
                      </div>
                    </div>
                  </div>
                </NavLink>
              </div>
            </Collapse>
          </div>

          <NavLink
            to="/contactus"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            <Group gap={10}>
              <PhoneCall size={18} className="main-nav__drawer-icon" />
              <span>Contact Us</span>
            </Group>
          </NavLink>

          <Divider my="md" />

          {!loggedInUser ? (
            <Stack gap="sm">
              <Button
                component={Link}
                to="/login"
                variant="filled"
                size="md"
                fullWidth
                leftSection={<LogIn size={18} />}
                className="main-nav__login-btn"
                onClick={closeDrawer}
              >
                Login
              </Button>
            </Stack>
          ) : (
            <Button
              variant="light"
              color="red"
              size="md"
              fullWidth
              leftSection={<LogOut size={18} />}
              onClick={() => {
                closeDrawer();
                logoutHandler();
              }}
            >
              Logout
            </Button>
          )}
        </Stack>
      </Drawer>
    </>
  );
}

export default MainNavigation;
